import type { SourceNode, SourceFolder } from "../../features/tars/notebook-details/sidebar/sources-types";
import { isFolder, normalizeSourceType } from "../../features/tars/notebook-details/sidebar/sources-types";
import type { SourceListItem } from "../../features/tars/tars-hooks/useSourceList";
import { dataBankDisplayFieldsFromMore } from "../../features/tars/notebook-details/sidebar/add-source/connectDataBankNodeFields";

function getFileDisplayName(
  item: SourceListItem & { s3_key?: string; extension?: string },
  ext: string | undefined
): string {
  const raw = item.name?.trim();
  if (raw && raw.length > 0 && !/^\.?[a-z0-9]+$/i.test(raw)) return raw;
  const s3Key = item.s3_key;
  if (s3Key) {
    const base = s3Key.split("/").pop() ?? "";
    if (base) return base;
  }
  return ext ?? "file";
}

export function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function buildStateFromList(sources: SourceListItem[]): {
  nodes: Record<string, SourceNode>;
  rootIds: string[];
} {
  const nodes: Record<string, SourceNode> = {};
  const rootIds: string[] = [];
  const folderChildren: Record<string, string[]> = {};

  for (const item of sources) {
    const id = String(item.id);
    const parentId =
      item.parent_id != null && item.parent_id !== 0 ? String(item.parent_id) : null;
    if (item.type === "folder") {
      const folder: SourceFolder = {
        id,
        name: item.name,
        parentId,
        type: "folder",
        childrenIds: [],
      };
      nodes[id] = folder;
      if (parentId) {
        folderChildren[parentId] = folderChildren[parentId] ?? [];
        folderChildren[parentId].push(id);
      } else {
        rootIds.push(id);
      }
    } else {
      const itemWithExt = item as SourceListItem & { extension?: string; s3_key?: string };
      const ext = itemWithExt.extension ?? item.name?.split(".").pop();
      const more = (item as SourceListItem & { more?: Record<string, unknown> }).more;
      const s3Key = itemWithExt.s3_key;
      const file = {
        id,
        name: getFileDisplayName(itemWithExt, ext),
        parentId,
        type: normalizeSourceType(item.type) || "file",
        extension: ext,
        ...(more?.url != null && { url: String(more.url) }),
        ...(s3Key != null && s3Key !== "" && { s3_key: s3Key }),
        ...(typeof more?.es_index === "string" && more.es_index.trim() !== ""
          ? { es_index: more.es_index.trim() }
          : {}),
        ...dataBankDisplayFieldsFromMore(more),
      };
      nodes[id] = file;
      if (parentId) {
        folderChildren[parentId] = folderChildren[parentId] ?? [];
        folderChildren[parentId].push(id);
      } else {
        rootIds.push(id);
      }
    }
  }

  for (const [folderId, childIds] of Object.entries(folderChildren)) {
    const node = nodes[folderId];
    if (node && isFolder(node)) {
      (nodes[folderId] as SourceFolder).childrenIds = childIds;
    }
  }

  return { nodes, rootIds };
}

export function buildNodesForFolder(
  sources: SourceListItem[],
  parentId: string
): { nodes: Record<string, SourceNode>; childIds: string[] } {
  const nodes: Record<string, SourceNode> = {};
  const childIds: string[] = [];

  for (const item of sources) {
    const id = String(item.id);
    if (item.type === "folder") {
      const folder: SourceFolder = {
        id,
        name: item.name,
        parentId,
        type: "folder",
        childrenIds: [],
      };
      nodes[id] = folder;
      childIds.push(id);
    } else {
      const itemWithExt = item as SourceListItem & { extension?: string; s3_key?: string };
      const ext = itemWithExt.extension ?? item.name?.split(".").pop();
      const more = (item as SourceListItem & { more?: Record<string, unknown> }).more;
      const s3Key = itemWithExt.s3_key;
      const file = {
        id,
        name: getFileDisplayName(itemWithExt, ext),
        parentId,
        type: normalizeSourceType(item.type) || "file",
        extension: ext,
        ...(more?.url != null && { url: String(more.url) }),
        ...(s3Key != null && s3Key !== "" && { s3_key: s3Key }),
        ...(typeof more?.es_index === "string" && more.es_index.trim() !== ""
          ? { es_index: more.es_index.trim() }
          : {}),
        ...dataBankDisplayFieldsFromMore(more),
      };
      nodes[id] = file;
      childIds.push(id);
    }
  }

  return { nodes, childIds };
}
